import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export interface DropdownOption<T = number> {
  value: T;
  label: string;
  icon?: React.ReactNode;
  image?: string;
  suffix?: string;
  disabled?: boolean;
}

export interface DropdownProps<T = number> {
  label?: string;
  error?: string;
  placeholder?: string;
  options: DropdownOption<T>[];
  value?: T | T[];
  onChange?: (value: T | T[]) => void;
  onBlur?: () => void;
  disabled?: boolean;
  multiSelect?: boolean;
  prefixIcon?: React.ReactNode;
  className?: string;
}

// Icons from Figma design
const DropdownIcon: React.FC<{ className?: string }> = ({
  className = 'size-5',
}) => (
  <svg className={className} viewBox="0 0 20 20" fill="none">
    <path
      d="M5 7.5l5 5 5-5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CheckIcon: React.FC<{ className?: string }> = ({
  className = 'size-4',
}) => (
  <svg className={className} viewBox="0 0 16 16" fill="none">
    <path
      d="M13.5 4.5L6 12L2.5 8.5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Dropdown = <T = number,>({
  label,
  error,
  placeholder = 'Select an option',
  options = [],
  value,
  onChange,
  onBlur,
  disabled,
  multiSelect = false,
  prefixIcon,
  className = '',
}: DropdownProps<T>) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Match PhoneNumberInput panel height (~4 items)
  const panelMaxHeight = 210; // 4 * 40px item height + padding

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        onBlur?.();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onBlur]);

  // Check if dropdown should open upward
  const checkOpenDirection = () => {
    if (!dropdownRef.current) return;

    const rect = dropdownRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;

    // Add some padding for safety
    const requiredSpace = panelMaxHeight + 8; // panel height + gap

    if (spaceBelow < requiredSpace && spaceAbove > spaceBelow) {
      setOpenUpward(true);
    } else {
      setOpenUpward(false);
    }
  };

  // Handle option selection
  const handleOptionSelect = (optionValue: T) => {
    if (disabled) return;

    if (multiSelect) {
      const currentValues = Array.isArray(value) ? value : [];
      const newValues = currentValues.includes(optionValue)
        ? currentValues.filter((v) => v !== optionValue)
        : [...currentValues, optionValue];
      onChange?.(newValues);
    } else {
      onChange?.(optionValue);
      setIsOpen(false);
    }
  };

  // Handle dropdown toggle
  const handleToggle = () => {
    if (disabled) return;

    if (!isOpen) {
      checkOpenDirection();
    }
    setIsOpen(!isOpen);
  };

  // Base styles
  const getContainerStyles = () => {
    const base =
      'relative w-full box-border flex h-12 cursor-pointer items-center justify-start gap-2 rounded-lg border px-4 py-2 transition-all duration-200 bg-white';
    if (disabled)
      return `${base} border-border-disabled bg-bg-disabled cursor-not-allowed`;
    if (error) return `${base} border-error focus:ring-0.5 focus:ring-error`;
    if (isOpen) return `${base} border-primary ring-0.5 ring-primary`;
    return `${base} border-border-default hover:border-border-hover focus:border-primary focus:ring-0.5 focus:ring-primary`;
  };

  const getIconStyles = () => {
    if (disabled) return 'text-border-disabled';
    if (error) return 'text-error';
    if (isOpen) return 'text-primary';
    return 'text-secondary-text';
  };

  const getTextStyles = () => {
    if (disabled) return 'text-border-disabled';
    return 'text-primary-text';
  };

  const { t } = useTranslation();
  const placeholderText = placeholder || t('components.dropdown.placeholder');

  const displayValue = () => {
    if (multiSelect && Array.isArray(value)) {
      if (value.length === 0) return placeholderText;
      if (value.length === 1) {
        const opt = options.find((o) => o.value === value[0]);
        return opt?.label || placeholderText;
      }
      return (
        <div className="flex items-center gap-2">
          <span className="text-primary-text">
            {t('components.dropdown.selected')}
          </span>
          <div className="bg-bg-selected rounded-md px-2 py-1">
            <span className="text-primary text-sm leading-5">
              {value.length === options.length
                ? t('components.dropdown.all')
                : `${value.length}`}
            </span>
          </div>
        </div>
      );
    } else {
      const opt = options.find((o) => o.value === value);
      return opt?.label || placeholderText;
    }
  };

  return (
    <div className="flex w-full flex-col items-start gap-1">
      {label && (
        <label className="text-secondary-text w-full text-left leading-7 font-medium">
          {label}
        </label>
      )}

      <div className={`relative w-full ${className}`} ref={dropdownRef}>
        {/* Dropdown Trigger */}
        <div className={getContainerStyles()} onClick={handleToggle}>
          {/* Prefix Icon - show selected option's icon in single-select, or prefixIcon as fallback */}
          {(() => {
            if (!multiSelect && value) {
              const selectedOption = options.find((o) => o.value === value);
              if (selectedOption?.icon) {
                return (
                  <div
                    className={`flex size-5 items-center justify-center ${getIconStyles()}`}
                  >
                    {selectedOption.icon}
                  </div>
                );
              }
              if (selectedOption?.image) {
                return (
                  <div className="h-5 w-5 overflow-hidden rounded-full">
                    <img
                      src={selectedOption.image}
                      alt={selectedOption.label}
                      className="h-full w-full object-cover"
                    />
                  </div>
                );
              }
            }

            if (prefixIcon) {
              return (
                <div
                  className={`flex size-5 items-center justify-center ${getIconStyles()}`}
                >
                  {prefixIcon}
                </div>
              );
            }

            return null;
          })()}

          {/* Content */}
          <div className="flex flex-1 items-center">
            <div
              className={`leading-[24px] font-normal not-italic ${
                (multiSelect ? Array.isArray(value) && value.length > 0 : value)
                  ? getTextStyles()
                  : 'text-border-default'
              } w-full text-left`}
            >
              {displayValue()}
            </div>
          </div>

          {/* Dropdown Arrow */}
          <div
            className={`flex size-5 items-center justify-center ${getIconStyles()} transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          >
            <DropdownIcon />
          </div>
        </div>

        {/* Dropdown Panel */}
        {isOpen && (
          <div
            className={`shadow-dropdown absolute left-0 z-[9999] w-full rounded-lg bg-white p-2 ${
              openUpward ? 'bottom-full mb-1' : 'top-full mt-1'
            }`}
          >
            <ul
              className="scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent scrollbar-thumb-rounded-full flex flex-col gap-2 overflow-auto"
              style={{ maxHeight: panelMaxHeight }}
              role="listbox"
            >
              {/* Options */}
              {options.map((option, index) => {
                const isSelected = multiSelect
                  ? Array.isArray(value) && value.includes(option.value)
                  : value === option.value;

                return (
                  <li key={index} className="list-none">
                    <button
                      type="button"
                      onClick={() =>
                        !option.disabled && handleOptionSelect(option.value)
                      }
                      className={`flex h-10 w-full items-center gap-2 rounded-lg px-4 py-[6px] text-left text-sm leading-7 transition-colors ${
                        option.disabled
                          ? 'text-primary-text cursor-not-allowed bg-white opacity-50'
                          : multiSelect
                            ? 'hover:bg-primary-50 text-primary-text bg-white md:hover:cursor-pointer'
                            : isSelected
                              ? 'bg-primary text-on-primary md:hover:cursor-pointer'
                              : 'hover:bg-primary-50 text-primary-text bg-white md:hover:cursor-pointer'
                      }`}
                      role="option"
                      aria-selected={isSelected}
                    >
                      {/* Multi-select checkbox - only show in multi-select mode */}
                      {multiSelect && (
                        <div className="relative size-6">
                          <div
                            className={`absolute inset-0 rounded border-2 ${isSelected ? 'border-primary bg-primary' : 'border-border-disabled bg-transparent'}`}
                          />
                          {isSelected && (
                            <div className="absolute inset-0 flex items-center justify-center text-white">
                              <CheckIcon className="size-3" />
                            </div>
                          )}
                        </div>
                      )}

                      {/* Option icon or image */}
                      {option.icon && (
                        <div
                          className={`flex size-5 items-center justify-center ${
                            !multiSelect && isSelected
                              ? 'text-current'
                              : 'text-secondary-text'
                          }`}
                        >
                          {option.icon}
                        </div>
                      )}

                      {option.image && (
                        <div className="h-6 w-6 overflow-hidden rounded-full">
                          <img
                            src={option.image}
                            alt={option.label}
                            className="h-full w-full object-cover"
                          />
                        </div>
                      )}

                      {/* Option label */}
                      <span className="flex-1 leading-[22px] font-normal">
                        {option.label}
                      </span>

                      {/* Option suffix */}
                      {option.suffix && (
                        <span
                          className={`font-normal whitespace-pre ${
                            !multiSelect && isSelected
                              ? 'text-current'
                              : multiSelect
                                ? 'text-border-default'
                                : 'text-text-disabled'
                          }`}
                        >
                          {option.suffix}
                        </span>
                      )}
                    </button>

                    {/* Line separator between options for multi-select */}
                    {multiSelect && index < options.length - 1 && (
                      <div className="border-divider mx-4 border-b" />
                    )}
                  </li>
                );
              })}

              {/* No options available */}
              {options.length === 0 && (
                <li className="list-none">
                  <div className="flex w-full items-center justify-center rounded-lg py-4">
                    <span className="text-border-default leading-6 font-normal">
                      {t('components.dropdown.noOptions')}
                    </span>
                  </div>
                </li>
              )}
            </ul>
          </div>
        )}
      </div>

      {error && (
        <p className="text-error relative mt-1 shrink-0 text-left text-xs leading-4 font-normal not-italic">
          {error}
        </p>
      )}
    </div>
  );
};

export default Dropdown;
